<template>
	<card>
		<template slot="header">
			<h5 v-if="$slots.title || title" class="card-title">
				<slot name="title">
					{{title}}
				</slot>
			</h5>
		</template>
		<div>
			<div class="loading-spinner" v-if="isLoading">
				<vue-loaders-ball-spin-fade-loader color="white" scale="1.2"/>
			</div>
			<div class="ct-chart" v-else>
				<v-chart class="chart" :option="option"/>
			</div>
		</div>
	</card>
</template>
<script>
import Card from "./Card.vue";
import { mapActions, mapGetters } from "vuex";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart } from "echarts/charts";
import {
	TitleComponent,
	TooltipComponent,
	LegendComponent
} from "echarts/components";
import VChart  from "vue-echarts";
import QuerySetting from '@/mixins/querySetting';
import OctoberData from '@/mixins/octoberData';

use([
	CanvasRenderer,
	PieChart,
	TitleComponent,
	TooltipComponent,
	LegendComponent
]);

export default {
	name: "cost-predict-card",
	mixins: [QuerySetting, OctoberData],
	components: {
		Card,
		VChart
	},
	props: {
		title: {
			type: String,
			default: ""
		}
	},
	data() {
		return {
			isLoading: true,
			totalCost: 0,
			tariff: {
				day: 0,
				night: 0,
				gen: 0
			},
			option: {
				tooltip: {
					text: "test",
					show: true,
					backgroundColor: "#484E69",
					borderColor: "#484E69",
					formatter: function (d) {
						return `${d.name} \n £${d.value}`;
					},
					textStyle: {
						color: 'white'
					},
				},
				color: ["#ADD8E6", "#800000"],
				textStyle: {
					fontFamily: 'Nunito Sans Light'
				},
				legend: {
					orient: "vertical",
					top: '40%',
					right: '3%',
					itemWidth: 12,
					itemHeight: 12,
					selectedMode: false,
					textStyle: {
						color: 'white',
					},
					icon: 'rect',
					data: [
						"Day",
						"Night"
					]
				},
				series: [
					{
						type: "pie",
						radius: ['66%', '80%'],
						center: ['40%', '50%'],
						showEmptyCircle: false,
						emphasis: {
							focus: 'self',
							label: {
								show: true,
								fontSize: '20',
								fontWeight: 'bold',
								color: 'white',
								lineHeight: 13,
								formatter: function(d) {
									return `${d.name}\n\n£${d.value}`;
								}
							}
						},
						labelLine: {
							show: true
						},
						data: [],
					}
				]
			},
		};
	},
	computed: {
		...mapGetters({
			periodTime: 'settime/getPeriodTime',
			currentDayPower			: 'influx/getCurrentDayPower',
			currentNightPower		: 'influx/getCurrentNightPower',
			user: 'auth/getUser',
			defaultSiteId: 'auth/getDefaultSiteId',
			defaultSiteName: 'auth/defaultSiteName'
		})
	},
	watch: {
		async periodTime(newValue) {
			await this.getTariff(this.user.siteId);
			this.getCostData();
		},
		async defaultSiteId(newValue) {
			await this.getTariff(this.user.siteId);
			this.getCostData();
		}
	},
	
	methods: {
		...mapActions({
			getDisplayData: 'influx/getDisplayData',
			getRates: "admin/getRates",
		}),
		getLabel() {
			return `Total\n\n£${this.totalCost}`;
		},
		async getTariff(site) {
			const rates = await this.getRates(site);
			this.tariff = {
				day: rates[rates.length - 1].cost_1,
				night: rates[rates.length - 1].cost_2,
				gen: rates[rates.length - 1].cost_3
			}
		},
		async getCostData() {
			this.isLoading = true;
			let dayTotal = 0.0;
			let nightTotal = 0.0;
			let dayCost = 0.0;
			let nightCost = 0.0;
			await this.getDisplayData('overall');
			this.option.series[0].data = [];
			let _dayPower = this.currentDayPower;
			let _nightPower = this.currentNightPower;

			_dayPower.forEach(d => dayTotal += d[1]);
			dayCost = dayTotal * this.tariff.day;
			this.option.series[0].data.push({
				value: Math.round((dayCost + Number.EPSILON) * 100) / 100,
				name: 'Day'
			});

			_nightPower.forEach(d =>  nightTotal += d[1]);
			nightCost = nightTotal * this.tariff.night;
			this.option.series[0].data.push({
				value: Math.round((nightCost + Number.EPSILON) * 100) / 100,
				name: 'Night'
			});

			this.isLoading = false;
			this.totalCost = 0;
			this.totalCost = Math.round((nightCost + dayCost + Number.EPSILON) * 100) / 100;
			this.option.series[0].label = {
				position: 'center',
				fontSize: '20',
				fontWeight: 'bold',
				color: 'white',
				lineHeight: 13,
				formatter: this.getLabel()
			}
		}
	},
	async mounted() {
		await this.getTariff(this.user.siteId);
		this.getCostData();
		this.$nextTick(function () {
            window.setInterval(() => {
				this.getCostData();
            },30000);
        });
	}
};
</script>
<style lang="scss" scoped>
.chart {
	height: 250px;
}
.total-value {
	position: absolute;
	top: 40%;
	left: 110px;
	text-align: center;

	span {
		font-size: 22px;
		font-family: 'Nunito Sans Regular';
		font-weight: 600;
		line-height: 1.1;
	}
}
</style>
